import { useNavigate, useParams } from 'react-router-dom'
import {
  Briefcase02,
  FileSearch02,
  MarkerPin01,
  Phone,
  Stars01,
} from '@untitled-ui/icons-react'
import { useWorkforce } from '../../../../../../Context'
import RiskSignalCard from './RiskSignalCard'

// Map risk_background_signals key → card config + empty-state description shown
// when the AI rationale is not present (AI errored or CLIK returned no parseable
// data). The count stays deterministic; only the rationale copy is empty-stated.
const SIGNAL_DEFS = [
  {
    key: 'phone_numbers',
    icon: Phone,
    title: 'Phone Numbers',
    highlightText: 'contact records',
    emptyState: 'No phone number insights available yet.',
    color: 'text-green-400',
  },
  {
    key: 'address_records',
    icon: MarkerPin01,
    title: 'Address Records',
    highlightText: 'address',
    emptyState: 'No address insights available yet.',
    color: 'text-blue-light/600',
  },
  {
    key: 'employment_records',
    icon: Briefcase02,
    title: 'Employment Records',
    highlightText: 'employment',
    emptyState: 'No employment insights available yet.',
    color: 'text-fuchsia-600',
  },
  {
    key: 'footprint',
    icon: FileSearch02,
    title: 'Footprints',
    highlightText: 'Footprints enquiry',
    emptyState: 'No credit enquiry insights available yet.',
    color: 'text-yellow-600',
  },
]

export default function RiskSignalsSection() {
  const navigate = useNavigate()
  const { id } = useParams()
  const { workforceDetailReport } = useWorkforce()
  const signalsApi = workforceDetailReport?.risk_background_signals ?? {}

  const handleViewDetails = (signal) => {
    navigate(`/workforce/employee/${id}/risk-signals?tab=${signal.title}`)
  }

  // Older snapshots ship bare numbers for each signal (e.g. phone_numbers: 4).
  // The new shape wraps each signal as { count, rationale }. We accept both
  // so partially-baked records still render counts even when the AI rationale
  // hasn't been produced yet.
  const signals = SIGNAL_DEFS.map((def) => {
    const cell = signalsApi[def.key]
    const isObj = cell && typeof cell === 'object'
    const count = Number(isObj ? cell.count : cell) || 0
    const apiRationale = isObj ? cell.rationale : null
    const description = apiRationale || def.emptyState
    return {
      icon: def.icon,
      title: def.title,
      count: String(count),
      highlightText: def.highlightText,
      description,
      color: def.color,
    }
  })

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          Risk & background signals
          <Stars01 className="h-5 w-5 text-brand/900" />
        </h3>
        <p className="text-sm text-gray-500">Aggregated records used to assess potential risk.</p>
      </div>

      <div className="flex flex-col gap-5">
        {signals.map((signal, index) => (
          <RiskSignalCard key={index} {...signal} onClick={() => handleViewDetails(signal)} />
        ))}
      </div>
    </div>
  )
}
